/**
 * Python code templates for macro script generation.
 *
 * Provides reusable code snippets that are assembled by the generator
 * into complete, runnable Python scripts.
 */

/**
 * Script header with imports and pip install comment.
 */
export const SCRIPT_HEADER = `#!/usr/bin/env python3
"""
Data Exchange Registry (DEX) Automation Script

This script reproduces API workflow actions captured from the DEX UI.
It handles encryption/decryption using PyNaCl's SealedBox.

Generated by DEX Macro Recorder.

Dependencies:
    pip install httpx pynacl

Usage:
    python script.py --api-key YOUR_API_KEY [options] [payload_content]

For help on all options, run:
    python script.py --help
"""

import argparse
import base64
import sys
from pathlib import Path

import httpx
from nacl.public import PrivateKey, PublicKey, SealedBox
`;

/**
 * DEXClient class with core HTTP and crypto methods.
 */
export const DEX_CLIENT_CLASS = `

class DEXClient:
    """
    Client for interacting with the Data Exchange Registry API.

    Handles authentication, HTTP requests, and payload encryption/decryption
    using NaCl sealed box cryptography.
    """

    def __init__(self, base_url: str, api_key: str, verbose: bool = False):
        """
        Initialize the DEX client.

        Args:
            base_url: The base URL of the DEX API (e.g., 'https://api.example.com')
            api_key: Your DEX API key for authentication
            verbose: If True, print request details to stderr
        """
        self.base_url = base_url.rstrip('/')
        self.headers = {
            'X-API-Key': api_key,
            'Content-Type': 'application/json'
        }
        self.verbose = verbose

    def _request(self, method: str, path: str, **kwargs) -> dict:
        """
        Make an authenticated API request.

        Args:
            method: HTTP method (GET, POST, PUT, DELETE)
            path: API path (e.g., '/payloads/sample/stage')
            **kwargs: Additional arguments passed to httpx.request

        Returns:
            Parsed JSON response as a dictionary

        Raises:
            httpx.HTTPStatusError: If the request fails
        """
        url = f"{self.base_url}{path}"
        if self.verbose:
            print(f"[DEX] {method} {url}", file=sys.stderr)
        with httpx.Client() as client:
            response = client.request(method, url, headers=self.headers, **kwargs)
            if self.verbose:
                print(f"[DEX] {response.status_code}", file=sys.stderr)
            response.raise_for_status()
            return response.json() if response.content else {}

    def get_reader_public_key(self, reader_id: str) -> str:
        """
        Fetch a reader's public key for encryption.

        Args:
            reader_id: The reader's party ID

        Returns:
            Base64-encoded public key string
        """
        path = f"/api/parties/{reader_id}/public-key"
        response = self._request("GET", path)
        return response["public_key"]

    def encrypt_payload(self, content: bytes, public_key_b64: str) -> str:
        """
        Encrypt content using a recipient's public key (sealed box).

        The sealed box provides anonymous encryption - the recipient can
        decrypt, but cannot determine who sent the message.

        Args:
            content: The plaintext content to encrypt
            public_key_b64: Base64-encoded recipient public key

        Returns:
            Base64-encoded encrypted ciphertext
        """
        public_key_bytes = base64.b64decode(public_key_b64)
        public_key = PublicKey(public_key_bytes)
        sealed_box = SealedBox(public_key)
        encrypted = sealed_box.encrypt(content)
        return base64.b64encode(encrypted).decode('ascii')

    def decrypt_payload(self, ciphertext_b64: str, private_key_b64: str) -> bytes:
        """
        Decrypt content using the recipient's private key.

        Args:
            ciphertext_b64: Base64-encoded encrypted payload
            private_key_b64: Base64-encoded private key

        Returns:
            Decrypted plaintext bytes
        """
        ciphertext = base64.b64decode(ciphertext_b64)
        private_key_bytes = base64.b64decode(private_key_b64)
        private_key = PrivateKey(private_key_bytes)
        sealed_box = SealedBox(private_key)
        return sealed_box.decrypt(ciphertext)

    def put_payload(
        self,
        sample_id: str,
        stage_id: str,
        reader_id: str,
        content: bytes,
        workflow_id: str | None = None
    ) -> dict:
        """
        Send an encrypted payload to a reader.

        Automatically fetches the reader's public key and encrypts the content.

        Args:
            sample_id: Sample identifier
            stage_id: Stage/step identifier
            reader_id: Target reader's party ID
            content: Plaintext content to encrypt and send
            workflow_id: Optional workflow ID (omit for 'any' workflow)

        Returns:
            API response with payload metadata
        """
        # Fetch reader's public key and encrypt content
        public_key = self.get_reader_public_key(reader_id)
        encrypted_payload = self.encrypt_payload(content, public_key)

        # Build API path
        if workflow_id:
            path = f"/api/payloads/{workflow_id}/{sample_id}/{stage_id}"
        else:
            path = f"/api/payloads/{sample_id}/{stage_id}"

        # Send encrypted payload
        return self._request("PUT", path, json={
            "reader_id": reader_id,
            "payload": encrypted_payload
        })

    def get_payload(
        self,
        sample_id: str,
        stage_id: str,
        workflow_id: str | None = None,
        private_key_b64: str | None = None
    ) -> dict:
        """
        Retrieve a payload, optionally decrypting it.

        Args:
            sample_id: Sample identifier
            stage_id: Stage/step identifier
            workflow_id: Optional workflow ID (omit for 'any' workflow)
            private_key_b64: If provided, decrypt the payload

        Returns:
            Payload metadata. If private_key_b64 provided, includes
            'decrypted_content' with the plaintext.
        """
        # Build API path
        if workflow_id:
            path = f"/api/payloads/{workflow_id}/{sample_id}/{stage_id}"
        else:
            path = f"/api/payloads/{sample_id}/{stage_id}"

        # Fetch payload
        response = self._request("GET", path)

        # Decrypt if private key provided
        if private_key_b64 and "payload" in response:
            decrypted = self.decrypt_payload(response["payload"], private_key_b64)
            response["decrypted_content"] = decrypted

        return response

    def mark_complete(
        self,
        sample_id: str,
        stage_id: str,
        workflow_id: str | None = None
    ) -> dict:
        """
        Mark a payload as complete (reader has processed it).

        Args:
            sample_id: Sample identifier
            stage_id: Stage/step identifier
            workflow_id: Optional workflow ID (omit for 'any' workflow)

        Returns:
            API response confirming completion
        """
        # Build API path
        if workflow_id:
            path = f"/api/payloads/{workflow_id}/{sample_id}/{stage_id}/complete"
        else:
            path = f"/api/payloads/{sample_id}/{stage_id}/complete"

        return self._request("POST", path, json={})
`;

/**
 * Generate argparse section based on which operations are in the recording.
 *
 * @param {Object} options - Configuration for argparser generation
 * @param {boolean} options.hasPutPayload - Recording includes put_payload operations
 * @param {boolean} options.hasGetPayload - Recording includes get_payload operations
 * @param {boolean} options.hasMarkComplete - Recording includes mark_complete operations
 * @param {Set<string>} options.sampleIds - Unique sample IDs in recording
 * @param {Set<string>} options.stageIds - Unique stage IDs in recording
 * @param {Set<string>} options.workflowIds - Unique workflow IDs in recording
 * @param {Set<string>} options.readerIds - Unique reader IDs in recording
 * @param {string|null} options.defaultBaseUrl - Default API base URL (null = required)
 * @returns {string} Python argparse setup code
 */
export function generateArgparser(options) {
  const {
    hasPutPayload,
    hasGetPayload,
    sampleIds = new Set(),
    stageIds = new Set(),
    workflowIds = new Set(),
    readerIds = new Set(),
    defaultBaseUrl = null
  } = options;

  // Build description based on recorded operations
  const operations = [];
  if (hasPutPayload) operations.push('send payloads');
  if (hasGetPayload) operations.push('retrieve payloads');
  if (options.hasMarkComplete) operations.push('mark payloads complete');
  const description = operations.length > 0
    ? `Perform DEX operations: ${operations.join(', ')}.`
    : 'Perform DEX operations.';

  // Format default values for comments
  const formatDefaults = (ids, label) => {
    const arr = Array.from(ids).filter(Boolean);
    return arr.length > 0 ? `Recorded ${label}: ${arr.join(', ')}` : '';
  };

  let code = `

def parse_args():
    """Parse command-line arguments."""
    parser = argparse.ArgumentParser(
        description="${description}",
        formatter_class=argparse.RawDescriptionHelpFormatter
    )

    # Required arguments
    parser.add_argument(
        "--api-key",
        required=True,
        help="Your DEX API key"
    )
    parser.add_argument(
        "--base-url",
        ${defaultBaseUrl ? `default="${defaultBaseUrl}",` : 'required=True,'}
        help="${defaultBaseUrl ? `DEX API base URL (default: ${defaultBaseUrl})` : 'DEX API base URL (e.g., https://api.example.com)'}"
    )
    parser.add_argument(
        "-v", "--verbose",
        action="store_true",
        default=False,
        help="Show request details (URL, method, status)"
    )
`;

  // Add sample/stage args with recorded values as defaults or help text
  const sampleDefault = sampleIds.size === 1 ? Array.from(sampleIds)[0] : null;
  const stageDefault = stageIds.size === 1 ? Array.from(stageIds)[0] : null;
  const workflowDefault = workflowIds.size === 1 ? Array.from(workflowIds)[0] : null;

  code += `
    # Payload identifiers
    parser.add_argument(
        "--sample-id",
        ${sampleDefault ? `default="${sampleDefault}",` : ''}
        help="Sample identifier${sampleDefault ? ` (default: ${sampleDefault})` : ''}"
    )
    parser.add_argument(
        "--stage-id",
        ${stageDefault ? `default="${stageDefault}",` : ''}
        help="Stage identifier${stageDefault ? ` (default: ${stageDefault})` : ''}"
    )
    parser.add_argument(
        "--workflow-id",
        ${workflowDefault ? `default="${workflowDefault}",` : 'default=None,'}
        help="Workflow identifier (optional)${workflowDefault ? ` (default: ${workflowDefault})` : ''}"
    )
`;

  // Add reader arg if put_payload is used
  if (hasPutPayload) {
    const readerDefault = readerIds.size === 1 ? Array.from(readerIds)[0] : null;
    code += `
    # Writer arguments
    parser.add_argument(
        "--reader-id",
        ${readerDefault ? `default="${readerDefault}",` : ''}
        help="Target reader's party ID${readerDefault ? ` (default: ${readerDefault})` : ''}"
    )
    parser.add_argument(
        "payload_content",
        nargs="?",
        help="Payload content to send (reads from stdin if not provided)"
    )
`;
  }

  // Add private key arg if get_payload is used
  if (hasGetPayload) {
    code += `
    # Reader arguments
    parser.add_argument(
        "--private-key",
        help="Base64-encoded private key for decryption (optional)"
    )
    parser.add_argument(
        "-o", "--output",
        help="Output file for decrypted content (default: stdout)"
    )
`;
  }

  code += `
    return parser.parse_args()
`;

  return code;
}

/**
 * Generate main function from recorded actions.
 *
 * @param {Array} actions - Array of recorded action objects
 * @returns {string} Python main function code
 */
export function generateMainFunction(actions) {
  if (!actions || actions.length === 0) {
    return `

def main():
    """Main entry point."""
    args = parse_args()
    client = DEXClient(args.base_url, args.api_key, verbose=args.verbose)

    # No actions were recorded
    print("No operations to perform.")


if __name__ == "__main__":
    main()
`;
  }

  let code = `

def main():
    """Main entry point."""
    args = parse_args()
    client = DEXClient(args.base_url, args.api_key, verbose=args.verbose)
`;

  // Generate code for each action
  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    const { operation, params, success, error } = action;

    // Build the operation call
    let operationCode = '';

    if (operation === 'put_payload') {
      // Get payload content - from args or stdin
      operationCode = `
    # Operation ${i + 1}: Send payload to reader '${params.readerId || 'READER_ID'}'
    # Sample: ${params.sampleId || 'SAMPLE_ID'}, Stage: ${params.stageId || 'STAGE_ID'}`;

      if (params.workflowId) {
        operationCode += `\n    # Workflow: ${params.workflowId}`;
      }

      operationCode += `
    content = args.payload_content
    if content is None:
        content = sys.stdin.read()
    if isinstance(content, str):
        content = content.encode('utf-8')

    result = client.put_payload(
        sample_id=args.sample_id,
        stage_id=args.stage_id,
        reader_id=args.reader_id,
        content=content${params.workflowId ? `,\n        workflow_id=args.workflow_id` : ''}
    )
    print(f"Payload sent: {result}")
`;
    } else if (operation === 'get_payload') {
      operationCode = `
    # Operation ${i + 1}: Retrieve payload
    # Sample: ${params.sampleId || 'SAMPLE_ID'}, Stage: ${params.stageId || 'STAGE_ID'}`;

      if (params.workflowId) {
        operationCode += `\n    # Workflow: ${params.workflowId}`;
      }

      operationCode += `
    result = client.get_payload(
        sample_id=args.sample_id,
        stage_id=args.stage_id${params.workflowId ? `,\n        workflow_id=args.workflow_id` : ''},
        private_key_b64=args.private_key
    )

    if "decrypted_content" in result:
        decrypted = result["decrypted_content"]
        if args.output:
            Path(args.output).write_bytes(decrypted)
            print(f"Decrypted content written to: {args.output}")
        else:
            sys.stdout.buffer.write(decrypted)
    else:
        print(f"Payload retrieved (encrypted): {result}")
`;
    } else if (operation === 'mark_complete') {
      operationCode = `
    # Operation ${i + 1}: Mark payload complete
    # Sample: ${params.sampleId || 'SAMPLE_ID'}, Stage: ${params.stageId || 'STAGE_ID'}`;

      if (params.workflowId) {
        operationCode += `\n    # Workflow: ${params.workflowId}`;
      }

      operationCode += `
    result = client.mark_complete(
        sample_id=args.sample_id,
        stage_id=args.stage_id${params.workflowId ? `,\n        workflow_id=args.workflow_id` : ''}
    )
    print(f"Payload marked complete: {result}")
`;
    }

    // If the action failed, comment it out with error info
    if (!success) {
      const errorMsg = error || 'Unknown error';
      const lines = operationCode.split('\n');
      const commentedLines = lines.map(line =>
        line.trim() ? `    # ${line.trim()}` : ''
      ).join('\n');
      code += `
    # FAILED: ${operation} - Error: ${errorMsg}
    # The following operation failed during recording and is commented out:
${commentedLines}
`;
    } else {
      code += operationCode;
    }
  }

  code += `

if __name__ == "__main__":
    main()
`;

  return code;
}
